/**
 * Atlantic Theme, Swipe + Chevron Navigation, zoom compatible
 */
class AtlanticSwipeGallery {
  constructor(container) {
    this.container = container;
    this.viewport = container.querySelector('[data-product-gallery-viewport]');
    this.mediaItems = Array.from(container.querySelectorAll('[data-product-gallery-figure]'));
    this.thumbnails = Array.from(container.querySelectorAll('[data-product-gallery-thumbnail]'));

    // find initial selected, fallback to 0
    const initial = this.mediaItems.findIndex(el => el?.dataset?.productGallerySelected === 'true');
    this.currentIndex = initial > -1 ? initial : 0;

    this.touching = false;
    this.init();
  }

  init() {
    if (!this.viewport || this.mediaItems.length === 0) return;

    this.injectArrows();
    this.bindArrowEvents();
    this.bindThumbs();
    this.setupKeyboard();
    this.setupTouchSwipe();
    this.ensureVisible(this.currentIndex);
  }

  injectArrows() {
    if (this.viewport.querySelector('.product-gallery__nav')) return;
    const nav = document.createElement('div');
    nav.className = 'product-gallery__nav';
    nav.innerHTML = `
      <button class="product-gallery__nav-prev" aria-label="Previous image">
        <svg viewBox="0 0 24 24" width="24" height="24" aria-hidden="true">
          <path d="M15.41 7.41 14 6 8 12l6 6 1.41-1.41L10.83 12z" fill="currentColor"/>
        </svg>
      </button>
      <button class="product-gallery__nav-next" aria-label="Next image">
        <svg viewBox="0 0 24 24" width="24" height="24" aria-hidden="true">
          <path d="M8.59 16.59 10 18l6-6-6-6-1.41 1.41L13.17 12z" fill="currentColor"/>
        </svg>
      </button>
    `;
    this.viewport.appendChild(nav);
  }

  bindArrowEvents() {
    const prev = this.viewport.querySelector('.product-gallery__nav-prev');
    const next = this.viewport.querySelector('.product-gallery__nav-next');
    if (prev) prev.addEventListener('click', () => this.goPrev());
    if (next) next.addEventListener('click', () => this.goNext());
  }

  bindThumbs() {
    this.thumbnails.forEach((t, i) => {
      t.addEventListener('click', () => this.goTo(i));
    });
  }

  setupKeyboard() {
    // container is focusable in Atlantic, still listen on document for reliability
    document.addEventListener('keydown', e => {
      if (e.key === 'ArrowLeft') { e.preventDefault(); this.goPrev(); }
      if (e.key === 'ArrowRight') { e.preventDefault(); this.goNext(); }
    });
  }

  setupTouchSwipe() {
    let startX = 0, startY = 0, moved = false;

    // ignore if user is pinching or Photoswipe is open
    const zoomOpen = () => {
      const pswp = document.querySelector('.pswp');
      if (!pswp) return false;
      const hidden = pswp.getAttribute('aria-hidden');
      return hidden === 'false' || pswp.classList.contains('pswp--open');
    };

    this.viewport.addEventListener('touchstart', e => {
      if (zoomOpen()) return;
      if (e.touches.length > 1) return; // pinch, ignore
      this.touching = true;
      moved = false;
      startX = e.touches[0].clientX;
      startY = e.touches[0].clientY;
    }, { passive: true });

    this.viewport.addEventListener('touchmove', e => {
      if (!this.touching || zoomOpen()) return;
      if (e.touches.length > 1) return;
      moved = true;
    }, { passive: true });

    this.viewport.addEventListener('touchend', e => {
      if (!this.touching || zoomOpen()) { this.touching = false; return; }
      this.touching = false;
      if (!moved) return;

      const endX = e.changedTouches[0].clientX;
      const endY = e.changedTouches[0].clientY;
      const diffX = startX - endX;
      const diffY = startY - endY;

      // horizontal intent threshold
      if (Math.abs(diffX) > Math.abs(diffY) && Math.abs(diffX) > 50) {
        if (diffX > 0) this.goNext();
        else this.goPrev();
      }
    }, { passive: true });
  }

  goTo(index) {
    if (index < 0 || index >= this.mediaItems.length) return;
    this.ensureVisible(index);
    this.currentIndex = index;
    this.pauseVideos();
  }

  goNext() {
    const next = (this.currentIndex + 1) % this.mediaItems.length;
    this.goTo(next);
  }

  goPrev() {
    const prev = this.currentIndex === 0 ? this.mediaItems.length - 1 : this.currentIndex - 1;
    this.goTo(prev);
  }

  ensureVisible(index) {
    this.mediaItems.forEach((item, i) => {
      const active = i === index;
      item.dataset.productGallerySelected = active ? 'true' : 'false';
      item.setAttribute('aria-hidden', active ? 'false' : 'true');
    });

    this.thumbnails.forEach((thumb, i) => {
      const active = i === index;
      thumb.dataset.productGallerySelected = active ? 'true' : 'false';
      thumb.classList.toggle('is-active', active);
    });
  }

  pauseVideos() {
    this.container.querySelectorAll('video').forEach(v => { try { v.pause(); } catch(e){} });
  }
}

// init on all relevant Theme Editor events
function initAtlanticSwipeGalleries() {
  document.querySelectorAll('[data-product-gallery]').forEach(g => new AtlanticSwipeGallery(g));
}
document.addEventListener('DOMContentLoaded', initAtlanticSwipeGalleries);
document.addEventListener('shopify:section:load', initAtlanticSwipeGalleries);
document.addEventListener('shopify:section:select', initAtlanticSwipeGalleries);
document.addEventListener('shopify:section:reorder', initAtlanticSwipeGalleries);
